"use client";

import type { LeadFormFieldChangeHandler } from "./form-types";

import { useCallback, useState } from "react";
import { useLeadFormPersistedValues } from "./LeadFormPersistedValuesContext";

/**
 * Seeds a scalar string field from the machine's persisted values on mount —
 * the mechanism that survives the step region's unmount/remount when it
 * moves into a fullscreen takeover Dialog — and
 * reports every change back to the machine through `onFieldChange` so the
 * next mount can restore it again. Writes on empty too: a caller like phone
 * relies on the empty write to clear a previously persisted value.
 */
function usePersistedFieldState(
  fieldName: string | null,
  onFieldChange?: LeadFormFieldChangeHandler,
): [string, (value: string) => void] {
  const { persistedFieldValues } = useLeadFormPersistedValues();
  const [value, setValue] = useState(() =>
    fieldName ? (persistedFieldValues[fieldName] ?? "") : "",
  );

  const setPersistedValue = useCallback(
    (next: string) => {
      if (fieldName) {
        onFieldChange?.(fieldName, next);
      }
      setValue(next);
    },
    [fieldName, onFieldChange],
  );

  return [value, setPersistedValue];
}

export { usePersistedFieldState };
